import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X as XIcon, ImagePlus, Trash2, Plus } from "lucide-react";
import parseIngredientLines from "@/lib/parseIngredients";
import SuggestCaptionButton from "@/components/SuggestCaptionButton";
import PolishTitleButton from "@/components/PolishTitleButton";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const filePreview = (path) => (path ? `${BACKEND}/api/files/${path.replace(/^\/+/, "")}` : "");

const NON_TRUSTED_PROOF_LABELS = [
  "I own this",
  "I wore this",
  "I made this",
  "I visited this",
  "I've used this for 6+ months",
  "Gifted to me",
  "Paid partnership",
  "Affiliate link",
  "Idea only - I have not personally tried it",
];

const TRUSTED_PROOF_LABELS = [
  "Used them once",
  "Used them a few times",
  "Regular client (1+ year)",
  "I've used them for 6+ months",
  "First appointment coming up",
  "Used them, no longer do",
  "Idea only - I have not personally tried them",
];

const SEASONS = ["any", "spring", "summer", "fall", "winter"];

/**
 * Author-only inline editor for a branch's text fields. Category is locked
 * (server refuses category changes). Photos and shop items have their own
 * dedicated editors. Everything else the author supplied at plant time is
 * editable here.
 */
export default function IdeaEditPanel({ idea, onCancel, onSaved }) {
  const isRecipe = idea.category === "Recipes";
  const isTrusted = idea.category === "Trusted";
  const isHome = idea.category === "Home";
  const isTravel = idea.category === "Travel/Places";
  const isFaith = idea.category === "Faith";
  const proofOptions = isTrusted ? TRUSTED_PROOF_LABELS : NON_TRUSTED_PROOF_LABELS;

  const [form, setForm] = useState({
    title: idea.title || "",
    context: idea.context || "",
    summary: idea.summary || "",
    proof_label: idea.proof_label || "",
    servings: idea.servings || "",
    total_time: idea.total_time || "",
    prep_time_minutes: idea.prep_time_minutes ?? "",
    season: idea.season || "",
    provider_type: idea.provider_type || "",
    service_area: idea.service_area || "",
    website_url: idea.website_url || "",
    room_space: idea.room_space || "",
    place_location: idea.place_location || "",
    scripture_ref: idea.scripture_ref || "",
  });
  // Ingredients live in their own array so the row editor can splice/remove
  // without touching the rest of the form. Sent as `ingredients` on save.
  const [ingredients, setIngredients] = useState(
    Array.isArray(idea.ingredients) && idea.ingredients.length
      ? idea.ingredients.map((r) => ({ name: r.name || "", qty: r.qty || "", unit: r.unit || "" }))
      : []
  );
  const [saving, setSaving] = useState(false);

  // Photo editor — separate from the text fields because photos go through
  // their own endpoint (PATCH /ideas/{id}/photos). Local state so cancel is
  // clean; save is one call per changed side (text vs photos).
  const isTrustedCat = idea.category === "Trusted";
  const photoCap = isRecipe ? 1 : 10;
  const [photos, setPhotos] = useState(() => {
    if (Array.isArray(idea.photos) && idea.photos.length) return [...idea.photos];
    return idea.photo_path ? [idea.photo_path] : [];
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);
  const initialPhotos = Array.isArray(idea.photos) && idea.photos.length
    ? idea.photos.join("|")
    : (idea.photo_path || "");
  const photosDirty = photos.join("|") !== initialPhotos;

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // For recipes we only support one cover — replace whatever was there.
      // For everything else, append until we hit the cap.
      if (isRecipe) setPhotos([data.path]);
      else setPhotos((prev) => (prev.length < photoCap ? [...prev, data.path] : prev));
    } catch {
      toast("Couldn't upload that photo.");
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));
  const promoteCover = (idx) =>
    setPhotos((prev) => (idx === 0 ? prev : [prev[idx], ...prev.filter((_, i) => i !== idx)]));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setIng = (i, k, v) => setIngredients((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addIng = () => setIngredients((rows) => [...rows, { name: "", qty: "", unit: "" }]);
  const removeIng = (i) => setIngredients((rows) => rows.filter((_, idx) => idx !== i));

  // Paste-a-list flow — swap the little scratch textarea for a one-tap
  // "Paste ingredients" import. Handles bulleted lists, "1. …" numbered
  // lists, and single-paragraph "; and "-separated blobs. Detects qty/unit
  // via the same regex the backend uses so what you see in the rows here
  // matches what the shopping list rolls up later.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteMode, setPasteMode] = useState("replace"); // 'replace' | 'append'
  const applyPaste = () => {
    const rows = parseIngredientLines(pasteText);
    if (rows.length === 0) {
      toast("Nothing to import — paste one ingredient per line.");
      return;
    }
    setIngredients((prev) => (pasteMode === "replace" ? rows : [...prev.filter((r) => r.name || r.qty), ...rows]));
    setPasteOpen(false);
    setPasteText("");
    toast(`Imported ${rows.length} ingredient${rows.length === 1 ? "" : "s"}.`);
  };

  const save = async () => {
    if (!form.title.trim()) return toast("Title cannot be empty.");
    if (!form.context.trim()) return toast("Context cannot be empty.");
    if (!form.proof_label) return toast("Pick a proof-of-use label.");
    if (isTrustedCat && !form.provider_type.trim()) return toast("Trusted needs a provider type.");
    if (isTrustedCat && !form.service_area.trim()) return toast("Trusted needs a service area.");
    // Recipes / most categories require at least one cover photo. Trusted may go zero.
    if (photosDirty && !isTrustedCat && photos.length === 0) {
      return toast("Add a cover photo before saving.");
    }

    setSaving(true);
    try {
      // Only send fields relevant to this branch's category so we don't
      // smuggle recipe fields onto a Trusted branch (server rejects them
      // anyway, but keeping the payload clean helps).
      const payload = {
        title: form.title.trim(),
        context: form.context.trim(),
        summary: form.summary.trim(),
        proof_label: form.proof_label,
      };
      if (isRecipe) {
        payload.servings = form.servings.trim();
        payload.total_time = form.total_time.trim();
        payload.season = form.season || null;
        // Ingredients: drop empty rows, trim; empty list = clear all rows
        payload.ingredients = ingredients
          .map((r) => ({ name: (r.name || "").trim(), qty: (r.qty || "").trim(), unit: (r.unit || "").trim() }))
          .filter((r) => r.name);
        // Prep time — coerce to int or null so backend sees a clean value
        const pt = String(form.prep_time_minutes).trim();
        payload.prep_time_minutes = pt === "" ? null : Math.max(0, parseInt(pt, 10) || 0);
      }
      if (isTrusted) {
        payload.provider_type = form.provider_type.trim();
        payload.service_area = form.service_area.trim();
        payload.website_url = form.website_url.trim();
      }
      if (isHome) payload.room_space = form.room_space.trim();
      if (isTravel) payload.place_location = form.place_location.trim();
      if (isFaith) payload.scripture_ref = form.scripture_ref.trim();

      let updated = idea;
      // Photos go through their own endpoint. Fire only when dirty so we
      // don't overwrite untouched arrays on every save.
      if (photosDirty) {
        const { data: p } = await api.patch(`/ideas/${idea.id}/photos`, { photos });
        updated = { ...updated, ...p };
      }
      const { data } = await api.patch(`/ideas/${idea.id}`, payload);
      toast("Seed updated.");
      onSaved({ ...updated, ...data });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="idea-edit-panel"
      className="rounded-2xl border border-[#B79256]/40 bg-white/80 p-5 space-y-4"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-serif-display text-2xl text-[#2A2823]">Edit your seed</h3>
        <span className="accent-label text-[10px] text-[#2A2823]/50">
          category locked · {idea.category}
        </span>
      </div>

      {/* Photo editor — cover shot(s). Recipes cap at 1; everything else up
          to 10. Skipped entirely for Trusted since they may go photo-less. */}
      <div>
        <label className="accent-label text-[#B79256]">
          {isRecipe ? "Cover photo" : "Photos"}
          {isTrustedCat ? " (optional)" : ""}
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <div
              key={`${p}-${i}`}
              className="relative w-24 h-24 rounded-xl overflow-hidden bg-[#EDE5DA] group border border-[#B79256]/30"
              data-testid={`edit-photo-tile-${i}`}
            >
              <img
                src={filePreview(p)}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              {i === 0 && !isRecipe && photos.length > 1 && (
                <span className="absolute top-1 left-1 text-[9px] tracking-wider bg-[#3E4A37] text-[#F7F3ED] px-1.5 py-0.5 rounded">COVER</span>
              )}
              <button
                type="button"
                onClick={() => removePhoto(i)}
                aria-label="Remove photo"
                data-testid={`edit-photo-remove-${i}`}
                className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1 text-[#8a3a2a] shadow"
              >
                <Trash2 className="w-3 h-3" strokeWidth={2} />
              </button>
              {i > 0 && !isRecipe && (
                <button
                  type="button"
                  onClick={() => promoteCover(i)}
                  aria-label="Make cover"
                  data-testid={`edit-photo-cover-${i}`}
                  className="absolute bottom-1 left-1 right-1 text-[10px] bg-black/55 hover:bg-black/70 text-white rounded px-1.5 py-0.5"
                >
                  Make cover
                </button>
              )}
            </div>
          ))}
          {(photos.length < photoCap || isRecipe) && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="edit-photo-add"
              className="w-24 h-24 rounded-xl border border-dashed border-[#B79256]/50 bg-[#FBF8F1] hover:bg-white flex flex-col items-center justify-center text-[11px] text-[#4E5A3D] disabled:opacity-60"
            >
              <ImagePlus className="w-4 h-4 mb-1" />
              {uploadingPhoto ? "Uploading…" : (isRecipe && photos.length >= 1 ? "Replace" : "Add photo")}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPickPhoto}
            className="hidden"
            data-testid="edit-photo-file-input"
          />
        </div>
        {isRecipe && (
          <p className="mt-2 text-[11px] text-[#2A2823]/55">
            One cover photo per recipe — uploading a new one replaces the current shot.
          </p>
        )}
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <label className="accent-label text-[#B79256]">Title</label>
          <PolishTitleButton
            draft={{ title: form.title, category: idea.category, context: form.context }}
            onPick={(t) => setForm((f) => ({ ...f, title: t.slice(0, 200) }))}
            testid="polish-title-edit"
          />
        </div>
        <Input
          data-testid="edit-title"
          value={form.title}
          onChange={set("title")}
          maxLength={200}
          className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
        />
      </div>

      <div>
        <label className="accent-label text-[#B79256]">Proof of use</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {proofOptions.map((p) => (
            <button
              type="button"
              key={p}
              data-testid={`edit-proof-${p}`}
              onClick={() => setForm((f) => ({ ...f, proof_label: p }))}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                form.proof_label === p
                  ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                  : "bg-transparent text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/10"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="accent-label text-[#B79256]">
          {isRecipe ? "Story & Steps" : "Context"}
        </label>
        <Textarea
          data-testid="edit-context"
          value={form.context}
          onChange={set("context")}
          rows={isRecipe ? 14 : 8}
          className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
        />
        {isRecipe && (
          <p className="mt-1.5 text-[11px] text-[#2A2823]/55 italic leading-snug">
            Write your story on top. Add step-by-step directions under a line
            that starts with <span className="font-mono not-italic">## Steps</span>
            {" "}(or "## Instructions", "## Method", "## Directions"). Each numbered or dashed
            line becomes a step.
          </p>
        )}
      </div>

      {/* Optional short caption shown on feed cards instead of the Story
          preview. Especially useful for Recipes whose Story starts with
          "## Ingredients" — the card would otherwise dump raw list rows. */}
      <div>
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <label className="accent-label text-[#B79256]">
            Card caption <span className="text-[#2A2823]/50 font-normal ml-1">· optional</span>
          </label>
          <SuggestCaptionButton
            draft={{ title: form.title, category: idea.category, context: form.context }}
            onSuggest={(caption) => setForm((f) => ({ ...f, summary: caption.slice(0, 160) }))}
            testid="suggest-caption-edit"
          />
        </div>
        <p className="mt-1 text-[11px] text-[#2A2823]/55 italic leading-snug">
          One sentence for the feed card. Leave blank and Willoah picks a prose line from your Story.
        </p>
        <Input
          data-testid="edit-summary"
          value={form.summary}
          onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value.slice(0, 160) }))}
          placeholder={isRecipe ? "e.g. Fast weeknight dinner everyone eats." : "e.g. The pillow every guest asks about."}
          maxLength={160}
          className="mt-2 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
        />
        <p className="text-[11px] text-[#2A2823]/40 mt-1 text-right">{form.summary.length}/160</p>
      </div>

      {isRecipe && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="accent-label text-[#B79256]">Servings</label>
            <Input
              data-testid="edit-servings"
              value={form.servings}
              onChange={set("servings")}
              placeholder="4"
              maxLength={60}
              className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
            />
          </div>
          <div>
            <label className="accent-label text-[#B79256]">Total time</label>
            <Input
              data-testid="edit-total-time"
              value={form.total_time}
              onChange={set("total_time")}
              placeholder="45 min"
              maxLength={60}
              className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
            />
          </div>
          <div>
            <label className="accent-label text-[#B79256]">Season</label>
            <select
              data-testid="edit-season"
              value={form.season || ""}
              onChange={set("season")}
              className="mt-1 h-10 w-full rounded-xl border border-[#B79256]/40 bg-[#FBF8F1] px-3 text-sm text-[#2A2823]"
            >
              <option value="">any</option>
              {SEASONS.filter((s) => s !== "any").map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isRecipe && (
        <div className="rounded-xl border border-[#B79256]/40 bg-[#FBF8F1]/60 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <label className="accent-label text-[#B79256]">Ingredients</label>
              <p className="text-xs text-[#2A2823]/60 mt-1">Add these to be able to plan this recipe in Willoah Planner. Loose entries are fine ("a splash", "to taste").</p>
            </div>
            <div className="w-32">
              <label className="accent-label text-[#B79256]">Prep (min)</label>
              <Input
                data-testid="edit-prep-time-minutes"
                type="number"
                min={0}
                value={form.prep_time_minutes}
                onChange={set("prep_time_minutes")}
                placeholder="15"
                className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
              />
            </div>
          </div>
          {ingredients.length === 0 ? (
            <p className="text-sm italic text-[#2A2823]/50">No ingredients yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="edit-ingredients-list">
              {ingredients.map((row, i) => (
                <li key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    data-testid={`edit-ingredient-qty-${i}`}
                    value={row.qty}
                    onChange={(e) => setIng(i, "qty", e.target.value)}
                    placeholder="2"
                    className="col-span-2 rounded-full border border-[#B79256]/40 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#B79256]/25"
                  />
                  <input
                    data-testid={`edit-ingredient-unit-${i}`}
                    value={row.unit}
                    onChange={(e) => setIng(i, "unit", e.target.value)}
                    placeholder="cups"
                    className="col-span-3 rounded-full border border-[#B79256]/40 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#B79256]/25"
                  />
                  <input
                    data-testid={`edit-ingredient-name-${i}`}
                    value={row.name}
                    onChange={(e) => setIng(i, "name", e.target.value)}
                    placeholder="flour"
                    className="col-span-6 rounded-full border border-[#B79256]/40 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#B79256]/25"
                  />
                  <button
                    type="button"
                    onClick={() => removeIng(i)}
                    data-testid={`edit-ingredient-remove-${i}`}
                    aria-label="Remove"
                    className="col-span-1 text-[#2A2823]/40 hover:text-[#8a3a2a] text-sm"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={addIng}
            data-testid="edit-ingredient-add"
            className="text-sm text-[#B79256] hover:text-[#4E5A3D] underline underline-offset-2"
          >
            + Add ingredient
          </button>
          <span className="text-[#B79256]/50 mx-1">·</span>
          <button
            type="button"
            onClick={() => setPasteOpen((o) => !o)}
            data-testid="edit-ingredient-paste-toggle"
            className="text-sm text-[#B79256] hover:text-[#4E5A3D] underline underline-offset-2"
          >
            {pasteOpen ? "Cancel paste" : "Paste a list"}
          </button>

          {pasteOpen && (
            <div
              className="mt-3 rounded-xl border border-[#B79256]/30 bg-[#FBF8F1] p-3 space-y-2"
              data-testid="edit-ingredient-paste-panel"
            >
              <label className="accent-label text-[#B79256] text-[10px]">
                Paste your list — one ingredient per line
              </label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                data-testid="edit-ingredient-paste-textarea"
                rows={6}
                placeholder={"e.g.\n8 eggs\n1/2 cup heavy whipping cream\n1/2 lb sausage\nVegetables (peppers or spinach)\nParmesan or cheddar cheese"}
                className="w-full rounded-lg border border-[#B79256]/30 bg-white px-3 py-2 text-sm text-[#2A2823] focus:outline-none focus:ring-2 focus:ring-[#B79256]/25 whitespace-pre-wrap font-mono"
                autoFocus
              />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-xs">
                  <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                    <input
                      type="radio"
                      name="paste-mode"
                      value="replace"
                      checked={pasteMode === "replace"}
                      onChange={() => setPasteMode("replace")}
                      data-testid="edit-ingredient-paste-mode-replace"
                    />
                    Replace existing
                  </label>
                  <label className="inline-flex items-center gap-1 text-[#2A2823]/70">
                    <input
                      type="radio"
                      name="paste-mode"
                      value="append"
                      checked={pasteMode === "append"}
                      onChange={() => setPasteMode("append")}
                      data-testid="edit-ingredient-paste-mode-append"
                    />
                    Add to list
                  </label>
                </div>
                <button
                  type="button"
                  onClick={applyPaste}
                  disabled={!pasteText.trim()}
                  data-testid="edit-ingredient-paste-apply"
                  className="inline-flex items-center gap-1 rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] disabled:opacity-40 px-4 h-9 text-sm"
                >
                  Import
                </button>
              </div>
              <p className="text-[11px] text-[#2A2823]/55">
                We'll auto-split each line into quantity, unit, and name — tweak any row after importing.
              </p>
            </div>
          )}
        </div>
      )}

      {isTrusted && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="accent-label text-[#B79256]">Provider type</label>
            <Input
              data-testid="edit-provider-type"
              value={form.provider_type}
              onChange={set("provider_type")}
              maxLength={120}
              className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
            />
          </div>
          <div>
            <label className="accent-label text-[#B79256]">Service area</label>
            <Input
              data-testid="edit-service-area"
              value={form.service_area}
              onChange={set("service_area")}
              maxLength={120}
              className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="accent-label text-[#B79256]">Website (optional)</label>
            <Input
              data-testid="edit-website-url"
              value={form.website_url}
              onChange={set("website_url")}
              placeholder="https://…"
              maxLength={400}
              className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
            />
          </div>
        </div>
      )}

      {isHome && (
        <div>
          <label className="accent-label text-[#B79256]">Room or space (optional)</label>
          <Input
            data-testid="edit-room-space"
            value={form.room_space}
            onChange={set("room_space")}
            placeholder="kitchen, mudroom, front porch"
            maxLength={80}
            className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
          />
        </div>
      )}

      {isTravel && (
        <div>
          <label className="accent-label text-[#B79256]">Where is it? (optional)</label>
          <Input
            data-testid="edit-place-location"
            value={form.place_location}
            onChange={set("place_location")}
            placeholder="Nashville, TN"
            maxLength={120}
            className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
          />
        </div>
      )}

      {isFaith && (
        <div>
          <label className="accent-label text-[#B79256]">Scripture reference (optional)</label>
          <Input
            data-testid="edit-scripture-ref"
            value={form.scripture_ref}
            onChange={set("scripture_ref")}
            placeholder="Psalm 23:1"
            maxLength={120}
            className="mt-1 rounded-xl border-[#B79256]/40 bg-[#FBF8F1]"
          />
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Button
          data-testid="edit-save"
          onClick={save}
          disabled={saving}
          className="rounded-full h-10 px-5 bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
        >
          <Check className="w-4 h-4 mr-2" strokeWidth={2}/> {saving ? "Saving…" : "Save changes"}
        </Button>
        <button
          type="button"
          data-testid="edit-cancel"
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-sm text-[#2A2823]/60 hover:text-[#2A2823]"
        >
          <XIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> cancel
        </button>
      </div>
    </div>
  );
}
